import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Search, Users, Download, Upload, Sparkles, FileSpreadsheet, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState, SkeletonRows, fmtDate } from "@/components/shared";
import { ComplianceBadge, PortalBadge, EmploymentBadge } from "@/components/ComplianceBadge";
import { WORKER_CATEGORIES, EMPLOYMENT_CONTEXTS, WORKER_LOCATIONS } from "@/lib/workerTypes";
import WorkerPinDialog from "@/components/WorkerPinDialog";
import { PageGuideButton } from "@/components/PageGuideButton";
import api from "@/lib/api";

export default function Employees() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [catF, setCatF] = useState("ALL");
  const [ctxF, setCtxF] = useState("ALL");
  const [locF, setLocF] = useState("ALL");
  const [plan, setPlan] = useState(null);
  const [ttActive, setTtActive] = useState(false);
  useEffect(() => { api.get("/employer/subscription").then((r) => setPlan(r.data.plan)).catch(() => {}); }, []);
  useEffect(() => { api.get("/employer/module/TIME_TRACKING").then((r) => setTtActive(r.data?.access !== "none")).catch(() => {}); }, []);
  const [pinFor, setPinFor] = useState(null);
  const openPin = (ev, emp) => { ev.preventDefault(); ev.stopPropagation(); setPinFor(emp); };

  const FILTERS = [
    { v: "ALL", l: t("tbl.fAll") }, { v: "COMPLIANT", l: t("tbl.fCompliant") }, { v: "ACTION_REQUIRED", l: t("tbl.fAction") },
    { v: "CRITICAL", l: t("tbl.fCritical") }, { v: "INCOMPLETE", l: t("tbl.fIncomplete") }, { v: "PORTAL_ACTIVE", l: t("tbl.fPortalActive") },
    { v: "PORTAL_INACTIVE", l: t("tbl.fPortalInactive") }, { v: "EXPIRING_30", l: t("tbl.fExp30") }, { v: "EXPIRING_90", l: t("tbl.fExp90") },
    { v: "ES_ACTIVE", l: t("tbl.fEsActive") }, { v: "ES_FREISTELLUNG", l: t("tbl.fEsFreistellung") }, { v: "ES_TERMINATED", l: t("tbl.fEsTerminated") },
  ];

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (filter && filter !== "ALL") params.set("filter", filter);
    const { data } = await api.get(`/employer/employees?${params}`);
    setRows(data.employees);
  }, [q, filter]);
  useEffect(() => { const timer = setTimeout(load, 200); return () => clearTimeout(timer); }, [load]);
  const shownRows = (rows || []).filter((e) =>
    (catF === "ALL" || (e.worker_category || "NOT_CLASSIFIED") === catF) &&
    (ctxF === "ALL" || (e.employment_context || "DIRECT_EMPLOYEE") === ctxF) &&
    (locF === "ALL" || (e.worker_location_status || "UNKNOWN") === locF));

  const fileRef = useRef();
  const exportPdf = async () => {
    try {
      const res = await api.get("/employer/workforce-report", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "employguard-workforce.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/employer/workforce.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "employguard-workforce.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/employer/employees/import", fd);
      toast.success(t("x.importDone", { n: r.data.created }));
      if (r.data.errors?.length) toast.error(r.data.errors.slice(0, 3).join(" · "));
      load();
    } catch { toast.error("Import failed"); }
    e.target.value = "";
  };
  const loadDemo = async () => {
    try { await api.post("/employer/init-demo"); toast.success(t("x.demoDone")); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader title={t("tbl.title")} subtitle={t("tbl.subtitle")}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PageGuideButton pageKey="employees" basePath="/employer" />
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-csv-input" />
            <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="import-csv-btn" title={t("x.importHint")}><Upload className="mr-1 h-4 w-4" /> {t("x.importCsv")}</Button>
            <Button variant="outline" className="rounded-full" onClick={exportPdf} data-testid="export-pdf-btn"><Download className="mr-1 h-4 w-4" /> {t("x.exportPdf")}</Button>
            <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="export-csv-btn"><FileSpreadsheet className="mr-1 h-4 w-4" /> {t("x.exportCsv")}</Button>
            <Link to="/app/employer/employees/new"><Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark" data-testid="add-employee-btn"><Plus className="mr-1 h-4 w-4" /> {t("tbl.add")}</Button></Link>
          </div>
        } />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("tbl.search")} className="pl-9" data-testid="employees-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-56" data-testid="employees-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={catF} onValueChange={setCatF}>
          <SelectTrigger className="w-full sm:w-44" data-testid="employees-filter-category"><SelectValue placeholder={t("p2.fCategory")} /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">{t("p2.allCat")}</SelectItem>{WORKER_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`wc.${c}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={ctxF} onValueChange={setCtxF}>
          <SelectTrigger className="w-full sm:w-44" data-testid="employees-filter-context"><SelectValue placeholder={t("p2.fContext")} /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">{t("p2.allCtx")}</SelectItem>{EMPLOYMENT_CONTEXTS.map((c) => <SelectItem key={c} value={c}>{t(`p2.ctx.${c}`)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={locF} onValueChange={setLocF}>
          <SelectTrigger className="w-full sm:w-44" data-testid="employees-filter-location"><SelectValue placeholder={t("p2.fLocation")} /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">{t("p2.allLoc")}</SelectItem>{WORKER_LOCATIONS.map((c) => <SelectItem key={c} value={c}>{t(`p2.loc.${c}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {plan && (plan.read_only || (plan.tier === "starter" && plan.employee_limit != null && filter === "ALL" && !q && (rows?.length ?? 0) >= plan.employee_limit)) && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="plan-limit-banner">
          <span className="text-sm text-amber-800">{plan.read_only ? t("bill.readOnly") : t("bill.limitReached", { n: plan.employee_limit })}</span>
          <Link to="/app/employer/billing"><Button size="sm" className="rounded-full bg-navy text-white" data-testid="banner-upgrade-btn">{t("bill.upgrade")}</Button></Link>
        </div>
      )}
      {rows === null ? <SkeletonRows /> :
        shownRows.length === 0 ? (
          <EmptyState icon={Users} title={t("tbl.emptyTitle")} testid="employees-empty" subtitle={t("tbl.emptySubtitle")}
            action={<div className="flex flex-wrap justify-center gap-2"><Link to="/app/employer/employees/new"><Button className="rounded-full bg-cyan text-white hover:bg-cyan-dark"><Plus className="mr-1 h-4 w-4" /> {t("tbl.add")}</Button></Link><Button variant="outline" className="rounded-full" onClick={loadDemo} data-testid="demo-data-btn"><Sparkles className="mr-1 h-4 w-4" /> {t("x.demoData")}</Button></div>} />
        ) : (
          <>
            <div className="card-soft hidden overflow-hidden lg:block">
              <table className="w-full text-sm" data-testid="employees-table">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="px-4 py-3">{t("tbl.colEmployee")}</th><th className="px-4 py-3">{t("tbl.colNationality")}</th><th className="px-4 py-3">{t("tbl.colJob")}</th>
                    <th className="px-4 py-3">{t("tbl.colPermit")}</th><th className="px-4 py-3">{t("tbl.colWorkAuth")}</th>
                    <th className="px-4 py-3">{t("tbl.colCompliance")}</th><th className="px-4 py-3">{t("tbl.colPortal")}</th><th className="px-4 py-3">{t("tbl.colNextAction")}</th>
                    {ttActive && <th className="px-4 py-3">PIN</th>}
                  </tr>
                </thead>
                <tbody>
                  {shownRows.map((e) => (
                    <tr key={e.id} onClick={() => nav(`/app/employer/employees/${e.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid={`employee-row-${e.id}`}>
                      <td className="px-4 py-3"><div className="flex items-center gap-2.5"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-navy text-[11px] font-bold text-white">{e.first_name[0]}{e.last_name[0]}</span><div><div className="flex items-center gap-1.5"><span className="font-semibold text-navy">{e.first_name} {e.last_name}</span><EmploymentBadge status={e.employment_status} /></div><div className="text-xs text-slate-400">{e.email || "—"}</div>{e.worker_id && <div className="text-[11px] text-slate-400" data-testid={`emp-workerid-${e.id}`}>Worker ID: {e.worker_id}</div>}</div></div></td>
                      <td className="px-4 py-3 text-slate-600">{e.nationality || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{e.job_title || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{fmtDate(e.permit_valid_until)}</td>
                      <td className="px-4 py-3 text-slate-600">{e.work_authorization_status || "—"}</td>
                      <td className="px-4 py-3"><ComplianceBadge status={e.compliance_status} /></td>
                      <td className="px-4 py-3"><PortalBadge status={e.portal_status} /></td>
                      <td className="px-4 py-3 text-xs text-cyan-dark">{e.next_action}</td>
                      {ttActive && <td className="px-4 py-3"><button onClick={(ev) => openPin(ev, e)} className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100" data-testid={`emp-pin-${e.id}`}><KeyRound className="mr-0.5 inline h-3.5 w-3.5" />PIN</button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 lg:hidden">
              {shownRows.map((e) => (
                <Link key={e.id} to={`/app/employer/employees/${e.id}`} className="card-soft block p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5"><span className="flex h-9 w-9 items-center justify-center rounded-full bg-navy text-xs font-bold text-white">{e.first_name[0]}{e.last_name[0]}</span><div><div className="flex items-center gap-1.5"><span className="font-semibold text-navy">{e.first_name} {e.last_name}</span><EmploymentBadge status={e.employment_status} /></div><div className="text-xs text-slate-400">{e.nationality} · {e.job_title || "—"}</div>{e.worker_id && <div className="text-[11px] text-slate-400">Worker ID: {e.worker_id}</div>}</div></div>
                    <ComplianceBadge status={e.compliance_status} />
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-slate-500"><span>{t("tbl.colPermit")}: {fmtDate(e.permit_valid_until)}</span><PortalBadge status={e.portal_status} /></div>
                  {ttActive && <button onClick={(ev) => openPin(ev, e)} className="mt-3 rounded-lg px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100" data-testid={`emp-pin-m-${e.id}`}><KeyRound className="mr-0.5 inline h-3.5 w-3.5" />PIN</button>}
                </Link>
              ))}
            </div>
          </>
        )}
      {pinFor && <WorkerPinDialog sourceType="internal" record={pinFor} onClose={() => setPinFor(null)} />}
    </div>
  );
}
